import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DYNAMICS_BUSINESS_CENTRAL_APP_ID,
  DYNAMICS_PROVIDER_ID,
  type ConnectionProviderKey,
} from "@forgepoint/shared";
import { useConnectionsStore } from "@/store/connectionsStore";

interface DynamicsConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
  title?: string;
  description?: string;
}

export function DynamicsConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
  title = "Connect Dynamics 365 Business Central",
  description = "Provide the tenant and company details for your Dynamics environment.",
}: DynamicsConnectionDialogProps) {
  const [tenantId, setTenantId] = useState("");
  const [environmentName, setEnvironmentName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [requiresEgressProxy, setRequiresEgressProxy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const fetchConnectionsByApp = useConnectionsStore(
    (state) => state.fetchConnectionsByApp,
  );

  useEffect(() => {
    if (!open) {
      setTenantId("");
      setEnvironmentName("");
      setCompanyName("");
      setRequiresEgressProxy(false);
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    const connectionProviderId: ConnectionProviderKey = DYNAMICS_PROVIDER_ID;
    const appId = DYNAMICS_BUSINESS_CENTRAL_APP_ID;
    if (!appId) {
      setError("Dynamics app mapping not found.");
      return;
    }

    const trimmedTenant = tenantId.trim();
    const trimmedEnvironment = environmentName.trim();
    const trimmedCompany = companyName.trim();

    if (!trimmedTenant || !trimmedEnvironment || !trimmedCompany) {
      setError("Please provide tenant ID, environment name, and company name.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const connection = await finalizeConnection({
        connectionProviderId,
        appId,
        credentials: {
          type: "DynamicsBusinessCentralServiceCredentials",
          tenantId: trimmedTenant,
        },
        config: {
          tenantId: trimmedTenant,
          environmentName: trimmedEnvironment,
          companyId: trimmedCompany,
          companyDisplayName: trimmedCompany,
        },
        requiresEgressProxy,
        name: `${trimmedEnvironment} / ${trimmedCompany}`,
      });

      if (!connection) {
        setError("Failed to create Dynamics connection. Please try again.");
        return;
      }

      toast.success("Dynamics connection created successfully.");
      await fetchConnectionsByApp(appId);
      onSuccess?.(connection.id);
      onOpenChange(false);
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message
          : "Failed to create Dynamics connection.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    tenantId,
    environmentName,
    companyName,
    requiresEgressProxy,
    finalizeConnection,
    fetchConnectionsByApp,
    onSuccess,
    onOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="dynamics-tenant-id">Tenant ID</Label>
            <Input
              id="dynamics-tenant-id"
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dynamics-environment">Environment Name</Label>
            <Input
              id="dynamics-environment"
              value={environmentName}
              onChange={(event) => setEnvironmentName(event.target.value)}
              placeholder="Production"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dynamics-company">Company Name</Label>
            <Input
              id="dynamics-company"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
              placeholder="Exact company name as shown in Business Central"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="dynamics-egress-proxy">
                Require static egress IP
              </Label>
              <p className="text-sm text-muted-foreground">
                Route all requests for this connection through Forgepoint's
                allowlistable IP.
              </p>
            </div>
            <Switch
              id="dynamics-egress-proxy"
              checked={requiresEgressProxy}
              onCheckedChange={setRequiresEgressProxy}
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Connecting..." : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
